import angular from 'angular';
import * as Configuration from 'src/ajs-app/app.constants.js';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';

const IS_INTERNAL_USER_KEY = 'isInternalUser';

export class UserService {

  constructor($http, Assert, LocalStorageFactory, $injector) {
    this.loggedUser = {};
    this.$http = $http;
    this.Assert = Assert;
    this.LocalStorageFactory = LocalStorageFactory;
    this.$injector = $injector;
  }

  loadLoggedUser(companyId) {
    return this.$http.get(Configuration.constants.baseUrl + 'api/2.0/companies/' + companyId + '/users/self');
  }

  setLoggedUser(loggedUser) {
    this.loggedUser = loggedUser;
    this.LocalStorageFactory.setItem(IS_INTERNAL_USER_KEY, loggedUser.email.endsWith('@alcmeon.com'));
  }

  storeCompanyUrlName(companyId, loggedUser) {
    const user = loggedUser || this.loggedUser;
    if (user) {
      const company = user.companies[0];
      if (company) {
        const { url_name } = company;
        this.LocalStorageFactory.setItem(`${companyId}-url-name`, url_name);
      }
    }
  }

  getCompanyUrlNameFromStorage(companyId) {
    if (companyId) {
      return this.LocalStorageFactory.getItem(`${companyId}-url-name`);
    }
    return;
  }

  /**
   * A logged user is considered to be an internal user if it's email ends with '@alcmeon.com'
   */
  isLoggedUserInternal() {
    return this.LocalStorageFactory.getItem(IS_INTERNAL_USER_KEY);
  }

  getLoggedUser() {
    return this.loggedUser;
  }

  clearLoggedUser() {
    this.loggedUser = {};
  }

  // ▼▼▼▼▼ migrated  ▼▼▼▼▼
  /**
   * @deprecated
   * Use getSortTypes in sort.service.ts
   */
  getSortTypes() {
    let user = this.loggedUser;
    if (!user) {
      return [];
    }
    return user.companies[0].sort_types;
  }

  /**
   * @deprecated
   * Use getRoutingTagsForTaskListFilters in filters.service.ts
   */
  getRoutingTagsForTaskListFilters(translations) {
    let user = this.loggedUser;
    if (!angular.isUndefined(user.routing_tags)) {
      let groupedRoutingTags = {};
      let routingTags = user.routing_tags;
      //we need to group them by group id
      angular.forEach(routingTags, function (routingTag, key) {
        if (angular.isUndefined(groupedRoutingTags[routingTag.group.name])) {
          //initialisation
          if (translations != null) {
            groupedRoutingTags[routingTag.group.name] = [{
              'id': null,
              'name': translations.all,
              'type': 'routing'
            }];
          } else {
            groupedRoutingTags[routingTag.group.name] = [];
          }
        }
        groupedRoutingTags[routingTag.group.name].push(routingTag);
      });
      //Once they are grouped together we output it as an array
      routingTags = [];
      angular.forEach(groupedRoutingTags, function (items, name) {
        routingTags.push({
          'name': name,
          'type': 'routing',
          'tag_index': ((items.length > 1) && !angular.isUndefined(items[1].group.tag_index)) ? items[1].group.tag_index : null,
          'items': items,
          'selected': null
        });
      });
      return routingTags;
    }
    return [];
  }
  // ▲▲▲▲▲ migrated ▲▲▲▲▲

  // ▼▼▼▼▼ not to migrate, because call is commented ▼▼▼▼▼
  getAccountFilter(companyId, translations) {
    let allowedSites = this.getAllowedSites();
    let formattedFilterSites = {
      'name': translations.account,
      'items': [],
      'type': 'account',
      'selected': null
    };
    formattedFilterSites.items.push({
      'id': null,
      'name': translations.all,
    });
    angular.forEach(allowedSites, function (site, key) {
      formattedFilterSites.items.push({
        'id': site.id,
        'name': site.name
      });
    });
    return [formattedFilterSites];
  }
  // ▲▲▲▲▲ not to migrate ▲▲▲▲▲

  getAllowedSites() {
    const sitesV2Map = this.$injector.get('AngularSitesService').sitesV2Map;
    let user = this.loggedUser;
    if (angular.isUndefined(user)) {
      return [];
    }
    if (user.capabilities) {
      return Object.keys(user.capabilities)
        .filter(siteId => user.capabilities[siteId]?.includes(CapabilityIdEnum.Answer))
        .map(siteId => sitesV2Map.get(+siteId))
        .filter(s => s);
    }
    return [];
  }

  hasCapability(siteId, capabilityId) {
    const user = this.loggedUser;

    if (user?.capabilities && siteId) {
      return user.capabilities[siteId]?.includes(capabilityId)
    }
    return false;
  }

  hasCapabilityForAtLeastOneEnabledSite(capabilityId) {
    return Object.keys(this.loggedUser.capabilities || {}).some((siteId) =>
      this.loggedUser.capabilities[siteId]?.includes(capabilityId))
  }

  /**
   * Determines if the user has only the read_only permission, and not any other permission, on all sites of a company
   */
  isLoggedUserReadOnlyForAllSites() {
    return Object.keys(this.loggedUser.capabilities || {}).every(this.isLoggedUserReadOnlyForSite);
  }

  isLoggedUserReadOnlyForSite = (siteId) => {
    return !this.loggedUser.capabilities || (this.loggedUser.capabilities[siteId]?.includes(CapabilityIdEnum.ReadOnly) && this.loggedUser.capabilities[siteId].length === 1);
  }

  defaultTimeZone() {
    return 'Europe/Paris';
  }

  // Returns the avatar url depending on whether the admin user is a bot or an agent
  getAvatarUrl(adminUserId) {
    let avatarUrl = '';
    try {
      const company = this.getLoggedUser().companies[0];

      if (company) {
        if (adminUserId === company.quick_reply_bot_id) {
          avatarUrl = 'ajs-assets/img/avatars/icon_bot.png'
        } else if (adminUserId === company.delayed_answers_bot_id) {
          avatarUrl = 'ajs-assets/img/avatars/icon_csurvey_bot.png'
        } else if (adminUserId === company.incoming_messages_bot_id
          || adminUserId === company.default_bot_id) {
          avatarUrl = 'ajs-assets/img/avatars/icon_triage_bot.png'
        } else if (adminUserId === company.marketing_bot_id) {
          avatarUrl = 'ajs-assets/img/avatars/icon_marketing_bot.png'
        } else {
          avatarUrl = 'ajs-assets/img/avatars/icon_conseiller.png'
        }
      } else {
        // Special case of changing company while on task process
        avatarUrl = '';
      }
    }
    catch {
      // Should have a property companies.
    }
    return avatarUrl;
  }


  emailForAdminUserId(userId) {
    return this.loggedUser.companies[0].usersMap.get(userId).email;
  }

  isLoggedUserPartOfTeam(tagId) {
    const loggedUser = this.getLoggedUser();

    // return true if no routing tag is defined for the company (or if somehow the DataFormatter's behaviour has changed)
    return loggedUser.routing_tags ? loggedUser.routing_tags.map(tag => tag.id).includes(tagId) : true;
  }

  canSeeActionErrorDetails() {
    return this.loggedUser.email.endsWith('@alcmeon.com');
  }

  static UserFactory($http, Assert, LocalStorageFactory, $injector) {
    return new UserService($http, Assert, LocalStorageFactory, $injector);
  }
}
UserService.UserFactory.$inject = ['$http', 'Assert', 'LocalStorageFactory', '$injector'];
